// The case types one service covers, each with an optional hourly minimum
// (null = the service minimum applies). Client twin of
// backend/src/provider-availability/service-case-types.util.ts: the save path
// normalizes with it, and every card composer withholds with it, so the
// builder preview and the public page print the same thing.
import {
  CASE_TYPE_VALUES,
  caseTypeLabel,
  normalizeCaseTypes,
} from '../../../utils/caseTypes';
import { publishedServiceFloors } from './serviceRateTypes';

export const MAX_SERVICE_CASE_TYPES = CASE_TYPE_VALUES.length;

// Never a case row: the service minimum IS the General minimum (services spec
// 2026-09-19, section 3).
const GENERAL = 'general';

const validRate = (value) => {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : null;
};

// Canonical slugs, first entry per type wins, unusable rates to null. A salary
// service carries no case minimums: they are hourly figures.
export function normalizeServiceCaseTypes(input, rateType) {
  if (!Array.isArray(input)) return [];
  const salary = rateType === 'salary';
  const out = [];
  for (const entry of input) {
    const raw = entry && typeof entry === 'object' ? entry.caseType : entry;
    const [caseType] = normalizeCaseTypes([raw]);
    if (!caseType || out.some((row) => row.caseType === caseType)) continue;
    out.push({
      caseType,
      minHourlyRate: salary ? null : validRate(entry?.minHourlyRate),
    });
    if (out.length >= MAX_SERVICE_CASE_TYPES) break;
  }
  return out;
}

// What a card may print: names always, minimums only where the service floor
// itself prints. Pass the same flag that nulls minHourlyRate beside it.
export function presentServiceCaseTypes(caseTypes, withhold) {
  return normalizeServiceCaseTypes(caseTypes).map((row) => ({
    caseType: row.caseType,
    minHourlyRate: withhold ? null : row.minHourlyRate,
  }));
}

export function sameServiceCaseTypes(a, b) {
  const x = normalizeServiceCaseTypes(a);
  const y = normalizeServiceCaseTypes(b);
  return (
    x.length === y.length
    && x.every((row, i) => row.caseType === y[i].caseType && row.minHourlyRate === y[i].minHourlyRate)
  );
}

// "Cardiac $295/hr" or "Cardiac" -- one entry as a person reads it.
export function caseTypeEntryLabel(row) {
  const label = caseTypeLabel(row.caseType);
  return row.minHourlyRate != null ? `${label} $${row.minHourlyRate}/hr` : label;
}

// The rows that count for a service (spec 2026-09-19, sections 3 and 4.2).
// Mirror of the backend util: canonical and de-duplicated; never a General
// row; and, when the provider's areas of proficiency are known, only the case
// types still among them (compared through the vocabulary, a value outside it
// by its text). A row whose proficiency was removed stays stored and simply
// stops counting. Pass null/undefined where the proficiencies are not at hand:
// every row then counts (the backend presenter has already filtered a public
// payload).
export function effectiveServiceCaseTypes(caseTypes, proficiencies, rateType) {
  const rows = normalizeServiceCaseTypes(caseTypes, rateType).filter(
    (row) => row.caseType !== GENERAL,
  );
  if (!Array.isArray(proficiencies)) return rows;
  const held = new Set(normalizeCaseTypes(proficiencies));
  return rows.filter((row) => held.has(row.caseType));
}

// Whether a card schedule block may print hourly figures at all.
const priced = (schedule) =>
  !!schedule && !schedule.rateWithheld && schedule.rateType !== 'salary';

// The lowest and highest hourly minimum a service holds: its service minimum
// and every case row's effective minimum (its own, or the service minimum when
// blank). Feeds the page's "From $X" and the Market Pulse range. `schedule` is
// a card schedule block whose caseTypes are already effective. Null when the
// rate is withheld, the service is salaried, or there is no service minimum.
export function serviceMinimumRange(schedule) {
  if (!priced(schedule)) return null;
  const floor = validRate(schedule.minHourlyRate);
  if (floor == null) return null;
  const minimums = [
    floor,
    ...normalizeServiceCaseTypes(schedule.caseTypes)
      .filter((row) => row.caseType !== GENERAL)
      .map((row) => row.minHourlyRate ?? floor),
  ];
  return { low: Math.min(...minimums), high: Math.max(...minimums) };
}

// Every minimum a service holds, for the "Employers see your minimums" fact:
// the hourly range (serviceMinimumRange, which stays hourly and is null for an
// annual lead) plus the annual floor the service also publishes, as
// annualLow/annualHigh (one figure, so they match). One key set for a one-floor
// service, exactly serviceMinimumRange's; null when the rate is withheld or
// nothing is published.
export function serviceMinimums(schedule) {
  const hourly = serviceMinimumRange(schedule);
  const annual = schedule && !schedule.rateWithheld ? publishedServiceFloors(schedule).annual : null;
  if (!hourly && annual == null) return null;
  return { ...(hourly || {}), ...(annual != null ? { annualLow: annual, annualHigh: annual } : {}) };
}

// A serviceMinimumRange as the page prints it: "From $215/hr" when the
// minimums span a range, "$245/hr" when they are all the same ("From" over a
// single figure promises a range that is not there). `suffix` is the unit,
// '/hr' in a row or card, '' on the thumbnail. Null without a range.
export function minimumRangeText(range, suffix = '') {
  if (!range) return null;
  return range.low === range.high
    ? `$${range.low}${suffix}`
    : `From $${range.low}${suffix}`;
}

// What a listing that names only case types the service does not list is
// checked against: the service's highest minimum, the service minimum and
// every row's effective one (backend serviceHourlyFloorFor). The page prints
// it as the "Other case types" line, so no case type reads as refused. Null
// without case rows (one minimum already covers any case type) or where no
// figure may print (rate withheld, salary, no service minimum).
export function otherCaseTypesMinimum(schedule) {
  if (!serviceCaseRows(schedule || {}).length) return null;
  return serviceMinimumRange(schedule)?.high ?? null;
}

export const OTHER_CASE_TYPES_LABEL = 'Other case types';

// Every effective row with the minimum a listing for it is checked against,
// plus General at the service minimum, highest first (a named case type ahead
// of General on a tie, stored order otherwise). With no figure to print (rate
// withheld, salary, or no service minimum) every minHourlyRate is null and the
// rows keep stored order with General last; a caller that prints names only
// drops General, which would just restate the hidden floor. [] when the
// service has no case rows: one minimum for any case, as before.
export function serviceCaseRows(schedule) {
  if (!schedule) return [];
  const rows = normalizeServiceCaseTypes(schedule.caseTypes).filter(
    (row) => row.caseType !== GENERAL,
  );
  if (!rows.length) return [];
  const floor = priced(schedule) ? validRate(schedule.minHourlyRate) : null;
  const all = [
    ...rows.map((row) => ({
      caseType: row.caseType,
      label: caseTypeLabel(row.caseType),
      minHourlyRate: floor == null ? null : (row.minHourlyRate ?? floor),
    })),
    { caseType: GENERAL, label: caseTypeLabel(GENERAL), minHourlyRate: floor },
  ];
  if (floor == null) return all;
  return all
    .map((row, index) => ({ row, index }))
    .sort((a, b) => b.row.minHourlyRate - a.row.minHourlyRate || a.index - b.index)
    .map(({ row }) => row);
}
